import { fetchApi } from '@/services/api-client';

import { REPORT_CARDS_PATH, REPORT_PERIODS_PATH } from './report-cards';

const EY_PATH = `${REPORT_CARDS_PATH}/early-years`;

export type EyRubric = { E: string; D: string; P: string; Ex: string };

export type EyIndicator = {
  id: string;
  gradeLevel: number;
  domain: string;
  /** Null for a custom indicator a teacher added under a category. */
  code: string | null;
  label: string;
  description: string;
  example: string | null;
  practice: string | null;
  suggestedQuarters: number[] | null;
  rubric: EyRubric | null;
  sortOrder: number;
  active: boolean;
};

export type EyIndicatorFields = {
  domain: string;
  code?: string | null;
  label: string;
  description: string;
  example?: string | null;
  practice?: string | null;
  suggestedQuarters?: number[] | null;
  rubric?: EyRubric | null;
};

export type EyEditableLevel = {
  gradeLevel: number;
  classId: string | null;
  className: string | null;
  canEdit: boolean;
  /** The caller is this level's homeroom teacher. */
  mine: boolean;
};

export type EyWorkspaceIndicator = {
  standardId: string;
  lineId: string;
  indicatorId: string | null;
  domain: string;
  code: string | null;
  label: string;
  description: string | null;
  example: string | null;
  practice: string | null;
  rubric: EyRubric | null;
};

export type EyReflection = {
  strengthsComment: string | null;
  growthComment: string | null;
  homeSupportComment: string | null;
};

export type EyWorkspaceChild = EyReflection & {
  cardId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  nickname: string | null;
  /** standardId → valueId */
  marks: Record<string, string>;
  previous: (EyReflection & { periodName: string }) | null;
};

export type EySummary = { id: string; title: string; body: string };

export type EyScaleValue = { id: string; label: string; title: string; descriptor: string | null };

export type EyWorkspace = {
  periodId: string;
  periodSequence: number;
  gradeLevel: number;
  classId: string | null;
  readOnly: boolean;
  scaleValues: EyScaleValue[];
  indicators: EyWorkspaceIndicator[];
  gradedStandardIds: string[];
  children: EyWorkspaceChild[];
  summaries: EySummary[];
  banner: { title: string | null; focus: string | null; purpose: string | null };
};

export type EyGradeSettings = {
  gradeLevel: number;
  bannerTitle: string | null;
  focus: string | null;
  purpose: string | null;
};

export type EyGradeSettingsData = Partial<Omit<EyGradeSettings, 'gradeLevel'>>;

const send = (method: string, body: unknown) => ({ method, body: JSON.stringify(body) });

export const earlyYearsApi = {
  levels: (periodId: string) =>
    fetchApi<EyEditableLevel[]>(`${EY_PATH}/levels?periodId=${encodeURIComponent(periodId)}`),
  listIndicators: (gradeLevel: number, includeInactive = false) =>
    fetchApi<EyIndicator[]>(
      `${EY_PATH}/indicators?gradeLevel=${gradeLevel}${includeInactive ? '&includeInactive=true' : ''}`
    ),
  createIndicator: (dto: EyIndicatorFields & { gradeLevel: number }) =>
    fetchApi<EyIndicator>(`${EY_PATH}/indicators`, send('POST', dto)),
  updateIndicator: (id: string, dto: Partial<EyIndicatorFields> & { active?: boolean }) =>
    fetchApi<EyIndicator>(`${EY_PATH}/indicators/${id}`, send('PATCH', dto)),
  importIndicators: (gradeLevel: number, rows: EyIndicatorFields[]) =>
    fetchApi<{ created: number; updated: number }>(`${EY_PATH}/indicators/import`, send('POST', { gradeLevel, rows })),
  workspace: (periodId: string, gradeLevel: number) =>
    fetchApi<EyWorkspace>(`${EY_PATH}/periods/${periodId}/grades/${gradeLevel}`),
  setIndicators: (periodId: string, gradeLevel: number, indicatorIds: string[]) =>
    fetchApi<EyWorkspace>(
      `${EY_PATH}/periods/${periodId}/grades/${gradeLevel}/indicators`,
      send('PUT', { indicatorIds })
    ),
  saveSummaries: (periodId: string, gradeLevel: number, items: { title: string; body: string }[]) =>
    fetchApi<EySummary[]>(`${EY_PATH}/periods/${periodId}/grades/${gradeLevel}/summaries`, send('PUT', { items })),
  saveStandardMark: (cardId: string, standardId: string, valueId: string | null) =>
    fetchApi<void>(`${EY_PATH}/cards/${cardId}/standards/${standardId}/mark`, send('PUT', { valueId })),
  saveReflection: (cardId: string, dto: Partial<EyReflection>) =>
    fetchApi<void>(`${EY_PATH}/cards/${cardId}/reflection`, send('PATCH', dto)),
  getGradeSettings: (periodId: string, gradeLevel: number) =>
    fetchApi<EyGradeSettings>(`${REPORT_PERIODS_PATH}/${periodId}/grade-settings/${gradeLevel}`),
  saveGradeSettings: (periodId: string, gradeLevel: number, dto: EyGradeSettingsData) =>
    fetchApi<EyGradeSettings>(`${REPORT_PERIODS_PATH}/${periodId}/grade-settings/${gradeLevel}`, send('PUT', dto)),
};
